import { useAuth, type AuthContextProps } from 'react-oidc-context';
import { devAuthUser } from '@config/env';

/**
 * The silent refresh currently in flight, shared by every caller.
 *
 * Module-level because there is exactly ONE session per tab, and the requests that race for it come
 * from all over the app — a dashboard paint fires the positions, stats and /me queries at once, and
 * any cache invalidation fires another burst.
 *
 * Without this, each of those calls `signinSilent()` independently the moment the token expires.
 * Cognito ROTATES the refresh token, so the first refresh invalidates the token the others are still
 * holding: one wins and the rest fail, return null, and their requests go out with no Authorization
 * header at all — which is a 401 on everything simultaneously, including /me, and looks exactly like
 * being signed out. Sharing one promise means N racing callers cost one refresh.
 */
let refreshInFlight: Promise<string | null> | null = null;

/** Test seam — the module-level promise would otherwise leak between cases. */
export function resetSilentRefresh(): void {
  refreshInFlight = null;
}

/**
 * THE silent refresh — one in flight per tab, shared by every caller. Resolves to the new access
 * token, or null when the refresh failed (never throws).
 *
 * Exported because `AuthGuard` spends the refresh token at boot for a remembered session
 * (2026-09-13), and the first API calls can race it; both must ride the same promise or Cognito's
 * refresh-token rotation turns the second attempt into a failure. Cleared in `finally` so a failed
 * refresh does not wedge the tab on a permanently rejected token — the next request after a
 * recovery (network back, clock corrected) gets a fresh attempt.
 */
export function renewSilently(auth: Pick<AuthContextProps, 'signinSilent'>): Promise<string | null> {
  refreshInFlight ??= auth.signinSilent()
    .then(refreshed => refreshed?.access_token ?? null)
    .catch(() => null)
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

/**
 * Returns a function that resolves the current access token, refreshing silently when needed.
 * In dev-auth mode, returns a no-op resolver — the dev <c>X-Dev-User</c> header takes the place of the JWT.
 *
 * The conditional hook call below is intentional and safe: <c>devAuthUser</c> is a module-level
 * constant fixed at import time, so the hook-call sequence is identical on every render of this hook.
 */
export function useAccessTokenProvider(): () => Promise<string | null> {
  if (devAuthUser) return devNoToken;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useAuth();
  return async () => {
    if (!auth.user) return null;
    if (!auth.user.expired) return auth.user.access_token ?? null;

    // First caller starts the refresh; everyone else awaits the same promise (see renewSilently).
    return renewSilently(auth);
  };
}

const devNoToken = async (): Promise<string | null> => null;
